import type { ListBuilderConfiguration } from '../types';

type ListColumns = ListBuilderConfiguration['lists'][number]['columns'];

/**
 * Direction value mapping
 */
const DIRECTION_VALUE_MAPPING = [
	{ value: 'inbound', displayText: 'Inbound' },
	{ value: 'outbound', displayText: 'Outbound' },
];

/**
 * Internal/External value mapping
 */
const INTERNAL_VALUE_MAPPING = [
	{ value: '1', displayText: 'Internal' },
	{ value: '0', displayText: 'External' },
	{ value: 'true', displayText: 'Internal' },
	{ value: 'false', displayText: 'External' },
];

/**
 * Get standard call history embedded columns
 */
const getCallHistoryEmbeddedColumns = (): ListColumns => [
	{
		id: 'call-date',
		headers: [{ id: 'call-date-header', text: 'Date' }],
		fields: [
			{
				id: 'date-field',
				dataConfig: {
					type: 'field' as const,
					field: { fieldName: 'date', objectTable: 'call_user' },
				},
				styleConfig: {
					size: 'sm' as const,
					colorRule: { type: 'default' as const },
					formatOptions: { dateFormat: 'datetime-medium' },
				},
				displayStyle: 'date' as const,
			},
		],
		sortable: true,
		width: 150,
		sortFields: [
			{
				direction: 'DESC' as const,
				fieldName: 'date',
				objectTable: 'call_user',
			},
		],
	},
	{
		id: 'user-name',
		headers: [{ id: 'user-name-header', text: 'User' }],
		fields: [
			{
				id: 'user-name-field',
				dataConfig: {
					type: 'field' as const,
					field: { fieldName: 'userName', objectTable: 'call_user' },
				},
				styleConfig: {
					size: 'sm' as const,
					colorRule: { type: 'default' as const },
				},
				displayStyle: 'text' as const,
			},
			{
				id: 'user-ext-line-break',
				dataConfig: { type: 'custom_text' as const, customText: '' },
				styleConfig: {
					size: 'sm' as const,
					colorRule: { type: 'default' as const },
				},
				displayStyle: 'line_break' as const,
			},
			{
				id: 'phone-extension-field',
				dataConfig: {
					type: 'field' as const,
					field: { fieldName: 'phoneExtensionId', objectTable: 'call_user' },
				},
				styleConfig: {
					size: 'xs' as const,
					colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
				},
				displayStyle: 'text' as const,
			},
		],
		sortable: true,
		width: 150,
	},
	{
		id: 'external-phone',
		headers: [
			{ id: 'external-phone-header', text: 'Phone' },
			{ id: 'phone-line-break-header', text: '' },
			{ id: 'direction-header', text: 'Direction' },
			{ id: 'direction-line-break-header', text: '' },
			{ id: 'internal-header', text: 'Internal/External' },
		],
		fields: [
			{
				id: 'external-phone-field',
				dataConfig: {
					type: 'field' as const,
					field: { fieldName: 'externalPartyPhone', objectTable: 'call_user' },
				},
				styleConfig: {
					size: 'sm' as const,
					colorRule: { type: 'default' as const },
				},
				displayStyle: 'phone' as const,
			},
			{
				id: 'phone-line-break',
				dataConfig: { type: 'custom_text' as const, customText: '' },
				styleConfig: {
					size: 'sm' as const,
					colorRule: { type: 'default' as const },
				},
				displayStyle: 'line_break' as const,
			},
			{
				id: 'direction-field',
				dataConfig: {
					type: 'field' as const,
					field: { fieldName: 'direction', objectTable: 'call_user' },
				},
				styleConfig: {
					size: 'xs' as const,
					colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
					valueMapping: DIRECTION_VALUE_MAPPING,
				},
				displayStyle: 'text' as const,
			},
			{
				id: 'direction-line-break',
				dataConfig: { type: 'custom_text' as const, customText: '' },
				styleConfig: {
					size: 'sm' as const,
					colorRule: { type: 'default' as const },
				},
				displayStyle: 'line_break' as const,
			},
			{
				id: 'internal-field',
				dataConfig: {
					type: 'field' as const,
					field: { fieldName: 'isInternal', objectTable: 'call_user' },
				},
				styleConfig: {
					size: 'xs' as const,
					colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
					valueMapping: INTERNAL_VALUE_MAPPING,
				},
				displayStyle: 'text' as const,
			},
		],
		sortable: false,
		width: 150,
	},
	{
		id: 'duration',
		headers: [{ id: 'duration-header', text: 'Duration' }],
		fields: [
			{
				id: 'duration-field',
				dataConfig: {
					type: 'field' as const,
					field: { fieldName: 'duration', objectTable: 'call_user' },
				},
				styleConfig: {
					size: 'sm' as const,
					colorRule: { type: 'default' as const },
					formatOptions: { customFormat: 'duration-seconds' },
				},
				displayStyle: 'text' as const,
			},
		],
		sortable: true,
		width: 100,
	},
	{
		id: 'notes',
		headers: [{ id: 'notes-header', text: 'Notes' }],
		fields: [
			{
				id: 'notes-field',
				dataConfig: {
					type: 'field' as const,
					field: { fieldName: 'notes', objectTable: 'call' },
				},
				styleConfig: {
					size: 'sm' as const,
					colorRule: { type: 'default' as const },
				},
				displayStyle: 'text' as const,
			},
		],
		sortable: false,
		width: 250,
	},
	{
		id: 'actions',
		headers: [{ id: 'actions-header', text: '' }],
		fields: [
			{
				id: 'actions-dropdown',
				dataConfig: { type: 'custom_text' as const, customText: '' },
				styleConfig: {
					size: 'md' as const,
					colorRule: { type: 'default' as const },
				},
				displayStyle: 'actions_dropdown' as const,
				dropdownActions: [
					{
						id: 'edit-action',
						label: 'Edit',
						iconName: 'edit',
						callbackName: 'editCall',
						variant: 'primary',
						visibility: 'visible',
						requiredPermission: 'clients:calls:update',
						// Hide edit/delete for calls imported from a phone integration —
						// their source-of-truth lives in the external system, and
						// letting users modify them here would silently fudge that data.
						conditionalVisibility: {
							field: 'externalId',
							condition: 'is_null',
						},
					},
					{
						id: 'delete-action',
						label: 'Delete',
						iconName: 'trash',
						callbackName: 'deleteCall',
						variant: 'danger',
						visibility: 'visible',
						requiredPermission: 'clients:calls:delete',
						conditionalVisibility: {
							field: 'externalId',
							condition: 'is_null',
						},
					},
				],
			},
		],
		sortable: false,
		width: 80,
		align: 'center',
	},
];

/**
 * Call History Client Embedded list configuration
 * Converted from JSON to TypeScript for type safety
 */
export const CALL_HISTORY_CLIENT_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'client-embedded-calls-today',
			cacheTTL: 300000,
			name: 'Today',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: getCallHistoryEmbeddedColumns(),
			defaultSortId: 'column-call-date-desc',
			filterRules: {
				callHistoryDateRange: {
					defaultValue: [
						{
							id: 'today',
							label: 'Today',
							data: { startDate: '{{DATE:0d}}', endDate: '{{DATE:0d}}' },
						},
					],
					// The tab name *is* the filter — don't let users widen it.
					locked: true,
				},
			},
		},
		{
			id: 'client-embedded-calls-30-days',
			cacheTTL: 300000,
			name: '30 Days',
			type: 'default',
			active: true,
			sortOrder: 1,
			columns: getCallHistoryEmbeddedColumns(),
			defaultSortId: 'column-call-date-desc',
			filterRules: {
				callHistoryDateRange: {
					defaultValue: [
						{
							id: 'last-30-days',
							label: 'Last 30 Days',
							data: { startDate: '{{DATE:-30d}}', endDate: '{{DATE:0d}}' },
						},
					],
					// The tab name *is* the filter — don't let users widen it.
					locked: true,
				},
			},
		},
		{
			id: 'client-embedded-calls-all',
			cacheTTL: 300000,
			name: 'All Dates',
			type: 'default',
			active: true,
			sortOrder: 2,
			columns: getCallHistoryEmbeddedColumns(),
			defaultSortId: 'column-call-date-desc',
			// Explicitly clear the date range — without this override the
			// config-level defaultFilters (last 30 days) would apply to this tab too.
			filterRules: {
				callHistoryDateRange: {
					defaultValue: [],
				},
			},
		},
	],
	tableSorts: [
		{
			id: 'column-call-date-desc',
			name: 'Date (Newest First)',
			fields: [
				{ direction: 'DESC', fieldName: 'date', objectTable: 'call_user' },
			],
		},
		{
			id: 'column-call-date-asc',
			name: 'Date (Oldest First)',
			fields: [
				{ direction: 'ASC', fieldName: 'date', objectTable: 'call_user' },
			],
		},
		{
			id: 'duration-desc',
			name: 'Duration (High to Low)',
			fields: [
				{ direction: 'DESC', fieldName: 'duration', objectTable: 'call_user' },
			],
		},
		{
			id: 'duration-asc',
			name: 'Duration (Low to High)',
			fields: [
				{ direction: 'ASC', fieldName: 'duration', objectTable: 'call_user' },
			],
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	filters: {
		activeFilters: [
			// PD-9630 — the list layer promotes the FIRST text filter to the
			// list's search input, so the search box leads.
			'callHistorySearch',
			'callHistoryDateRange',
			'callHistoryUser',
			'callHistoryDirection',
			'callHistoryInternalExternal',
		],
		defaultFilters: {
			callHistoryDateRange: {
				id: 'last-30-days',
				label: 'Last 30 days',
				data: { startDate: '{{DATE:-30d}}', endDate: '{{DATE:0d}}' },
			},
			callHistoryDirection: { id: 'All', label: 'All' },
			callHistoryInternalExternal: { id: 'All', label: 'All' },
		},
	},
	showResetButton: true,
	objects: [
		{
			baseUrl: '/call-history/:id',
			objectLabel: 'Call',
			objectTable: 'call_user',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'date', fieldLabel: 'Date' },
				{ fieldName: 'userName', fieldLabel: 'User' },
				{ fieldName: 'externalPartyPhone', fieldLabel: 'External Phone' },
				{ fieldName: 'duration', fieldLabel: 'Duration' },
			],
		},
		{
			// The rep's notes and the AI summary live on the parent `call` row,
			// which the endpoint already returns flat on every row. Declaring the
			// table here is what offers them in the list configurator's field
			// picker (PD-14407); see call_history.config.ts for why it carries no
			// `baseUrl` or `rowIdField`.
			objectLabel: 'Call Details',
			objectTable: 'call',
			selectedFields: [
				{ fieldName: 'notes', fieldLabel: 'Notes' },
				{ fieldName: 'aiSummary', fieldLabel: 'AI Summary' },
			],
		},
	],
};
